import { MutableRefObject, useCallback, useEffect, useRef } from 'react';
import { useIdleTimer } from 'react-idle-timer';

import { sendWhenOpen } from './useWorkspaceWebSocket';

const HEARTBEAT = 15_000;
const TIMEOUT = 30_000;

export const useWebSocketPresenceHeartbeat = (wsRef: MutableRefObject<WebSocket | null>) => {
    const lastStatusRef = useRef<'online' | 'away'>('away');
    const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const safeSend = useCallback(
        (status: 'online' | 'away', force = false) => {
            const nextStatus = document.hidden && status === 'online' ? 'away' : status;

            if (!force && lastStatusRef.current === nextStatus) {return;}

            if (debounceTimeout.current) {clearTimeout(debounceTimeout.current);}

            debounceTimeout.current = setTimeout(() => {
                if (wsRef.current) {
                    sendWhenOpen(wsRef.current, {
                        v: 1,
                        type: 'presence:heartbeat',
                        status: nextStatus,
                    });

                    lastStatusRef.current = nextStatus;
                }
            }, 200);
        },
        [wsRef],
    );

    useEffect(() => {
        const interval = setInterval(() => {
            safeSend(lastStatusRef.current, true);
        }, HEARTBEAT);

        safeSend('online', true);

        return () => {
            clearInterval(interval);
            if (debounceTimeout.current) {clearTimeout(debounceTimeout.current);}
        };
    }, [safeSend]);

    useIdleTimer({
        timeout: TIMEOUT,
        onIdle: () => safeSend('away'),
        onActive: () => safeSend('online'),
        onAction: () => {
            if (lastStatusRef.current === 'away') {
                safeSend('online');
            }
        },
        debounce: 250,
    });

    useEffect(() => {
        const handleVisibility = () => {
            if (document.hidden) {
                safeSend('away', true);
            } else {
                safeSend('online', true);
            }
        };

        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [safeSend]);
};
